import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Volume2, 
  CheckCircle2,
  Factory,
  Leaf,
  Beaker,
  Layers
} from 'lucide-react';
import { TestingResults } from '../components/products/TestingResults';
import { PilotPartners } from '../components/products/PilotPartners';

const RD_STEPS = [
  { step: '01', title: 'Feedstock Analysis', description: 'Study waste stream composition and material properties' },
  { step: '02', title: 'Biofabrication', description: 'Develop and test biological binding and processing methods' },
  { step: '03', title: 'Performance Testing', description: 'Validate material properties against industry standards' },
  { step: '04', title: 'Lifecycle Analysis', description: 'Ensure biodegradability and environmental safety' },
];

const DIFFERENTIATORS = [
  { num: '1', title: 'Made In India', text: 'Localized production using locally available waste. No extractive raw materials.' },
  { num: '2', title: '100% Biodegradable', text: 'No paint, chemical or synthetic binders. No petrochemical inputs.' },
  { num: '3', title: 'Low Energy Process', text: 'Grown at Room Temperature.' },
];

const ACOUSTIC_VALUE_PROPS = [
  'Low carbon alternative to synthetic materials',
  '100% biodegradable composition',
  'Circular feedstock sourcing',
  'Designed for urban interiors & offices'
];

const BIOBRICK_VALUE_PROPS = [
  'Proprietary technology using agricultural and urban waste',
  'Biodegradable, low carbon alternative to conventional bricks',
  'Designed for outdoor & infrastructure applications'
];

const ProductsPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="bg-[#E8E3DA] text-[#4A5D4E] border-none mb-4">
              <Factory className="w-3 h-3 mr-1" />
              Products in Development
            </Badge>
            <h1 className="text-5xl font-bold text-[#2C3E2F] mb-6">
              Our Innovation Pipeline
            </h1>
            <p className="text-xl text-[#5A6B5E] max-w-3xl mx-auto leading-relaxed">
              Currently at TRL 3 and advancing towards level 4, we have successfully developed 
              prototypes including blocks, panels and tiles.
            </p>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {/* Acoustic Panels */}
            <Card data-testid="product-acoustic-panels" className="border-[#DDD5C7] overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-48 md:h-64 overflow-hidden bg-[#e8e3da]">
                <img 
                  src="https://customer-assets-39nsmqrw.emergentagent.net/job_1ae1f3c8-2446-4e63-863d-78729cdfc50f/artifacts/8coxev7l_1.png" 
                  alt="Acoustic Decorative Wall Panels" 
                  className="w-full h-full object-contain hover:scale-105 transition-transform duration-500"
                />
              </div>
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-[#2C3E2F] flex items-center gap-2 text-lg">
                    <Volume2 className="w-5 h-5 flex-shrink-0" />
                    Acoustic/ Decorative Wall Panels
                  </CardTitle>
                  <Badge className="bg-[#A8C9A5] text-white">TRL 3/4</Badge>
                </div>
                <CardDescription className="text-[#5A6B5E]">
                  Sustainable sound-absorbing and decorative wall panels from agricultural residues and textile waste
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-[#2C3E2F] mb-2 text-sm">Value Proposition</h4>
                  <ul className="space-y-1.5">
                    {ACOUSTIC_VALUE_PROPS.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-[#5A6B5E] text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#A8C9A5] flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#2C3E2F] mb-2 text-sm">Applications</h4>
                  <p className="text-[#5A6B5E] text-sm">
                    Office spaces, studios, auditoriums, home interiors, cafes, and any space 
                    requiring acoustic treatment with sustainable credentials.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* BioBricks / Paver Blocks */}
            <Card data-testid="product-biobricks" className="border-[#DDD5C7] overflow-hidden hover:shadow-xl transition-shadow">
              <div className="h-64 overflow-hidden grid grid-cols-2">
                <div className="bg-[#6b6b6b] overflow-hidden">
                  <img 
                    src="https://customer-assets-39nsmqrw.emergentagent.net/job_1ae1f3c8-2446-4e63-863d-78729cdfc50f/artifacts/th45yi2g_paver%20blocks.jpeg" 
                    alt="BioBricks Paver Blocks" 
                    className="w-full h-full object-contain hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="bg-[#8a8a8a] overflow-hidden">
                  <img 
                    src="https://customer-assets-39nsmqrw.emergentagent.net/job_1ae1f3c8-2446-4e63-863d-78729cdfc50f/artifacts/ci64n4p7_pb1.jpeg" 
                    alt="Paver Block Close-up" 
                    className="w-full h-full object-contain hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-[#2C3E2F] flex items-center gap-2 text-lg">
                    <Layers className="w-5 h-5 flex-shrink-0" />
                    BioBricks / Paver Blocks
                  </CardTitle>
                  <Badge className="bg-[#A8C9A5] text-white">TRL 3/4</Badge>
                </div>
                <CardDescription className="text-[#5A6B5E]">
                  Proprietary bio-composite construction materials engineered from urban and industrial waste streams
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-[#2C3E2F] mb-2 text-sm">Value Proposition</h4>
                  <ul className="space-y-1.5">
                    {BIOBRICK_VALUE_PROPS.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-[#5A6B5E] text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#A8C9A5] flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-[#2C3E2F] mb-2 text-sm">Applications</h4>
                  <p className="text-[#5A6B5E] text-sm">
                    Pathways, driveways, garden walls, parking areas, public spaces, 
                    and urban infrastructure requiring durable, sustainable construction materials.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Biosorbents - HIDDEN: uncomment to re-enable
            ... biosorbent card content preserved ...
            */}
          </div>

          {/* Our Prototypes */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-[#2C3E2F] text-center mb-8">
              Our Prototypes
            </h2>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              <div className="group">
                <div className="overflow-hidden rounded-xl bg-[#e8e4de]">
                  <img 
                    src="https://customer-assets-39nsmqrw.emergentagent.net/job_1ae1f3c8-2446-4e63-863d-78729cdfc50f/artifacts/m9tqlv8x_dp1.jpeg"
                    alt="Lab testing - panels installed on wall"
                    className="w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="pt-4">
                  <p className="text-xs font-semibold text-[#D4A574] uppercase tracking-wider mb-1">Lab Testing</p>
                  <h3 className="text-lg font-bold text-[#2C3E2F] mb-1">From Lab to Real World</h3>
                  <p className="text-sm text-[#5A6B5E]">Testing our mycelium based materials in real world environments.</p>
                </div>
              </div>
              <div className="group">
                <div className="overflow-hidden rounded-xl bg-[#e8e4de]">
                  <img 
                    src="https://customer-assets-39nsmqrw.emergentagent.net/job_1ae1f3c8-2446-4e63-863d-78729cdfc50f/artifacts/t5w7t3uy_dp2.jpeg"
                    alt="ELCIA Tech Summit showcase"
                    className="w-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="pt-4">
                  <p className="text-xs font-semibold text-[#D4A574] uppercase tracking-wider mb-1">ELCIA Tech Summit</p>
                  <h3 className="text-lg font-bold text-[#2C3E2F] mb-1">Taking Biomaterials to the Spotlight</h3>
                  <p className="text-sm text-[#5A6B5E]">Showcasing our mycelium based materials at ELCIA Tech Summit 2026.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Key Differentiators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {DIFFERENTIATORS.map((d) => (
              <div key={d.num} className="text-center p-8">
                <div className="w-14 h-14 rounded-full bg-[#4A5D4E]/10 border-2 border-[#4A5D4E]/30 flex items-center justify-center mx-auto mb-5">
                  <span className="text-[#4A5D4E] font-bold text-lg">{d.num}</span>
                </div>
                <h3 className="text-xl font-bold text-[#2C3E2F] mb-3">{d.title}</h3>
                <p className="text-[#5A6B5E]">{d.text}</p>
              </div>
            ))}
          </div>

          <TestingResults />

          {/* Technology Readiness Level Explanation */}
          <div className="bg-[#F5F3EE] rounded-2xl p-8 md:p-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#A8C9A5]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Beaker className="w-6 h-6 text-[#4A5D4E]" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#2C3E2F] mb-2">What is TRL 3/4?</h3>
                <p className="text-[#5A6B5E] leading-relaxed">
                  Technology Readiness Level (TRL) 3/4 indicates that we have completed experimental 
                  proof of concept and are advancing towards laboratory-scale validation. We have successfully 
                  developed working prototypes and are now testing performance characteristics against 
                  industry standards.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-lg p-6">
                <div className="text-[#D4A574] font-bold mb-2">Current Stage</div>
                <div className="text-sm text-[#5A6B5E]">Prototype development and laboratory validation (TRL 3/4)</div>
              </div>
              <div className="bg-white rounded-lg p-6">
                <div className="text-[#D4A574] font-bold mb-2">Next Steps (2026)</div>
                <div className="text-sm text-[#5A6B5E]">Conduct pilot installations and field testing</div>
              </div>
              <div className="bg-white rounded-lg p-6">
                <div className="text-[#D4A574] font-bold mb-2">Timeline (2027)</div>
                <div className="text-sm text-[#5A6B5E]">Move towards early commercialization</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* R&D Process */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#2C3E2F] mb-4">
              Our R&D Process
            </h2>
            <p className="text-lg text-[#5A6B5E] max-w-3xl mx-auto">
              From waste stream to validated product, our research process is rigorous and science-backed.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {RD_STEPS.map((item) => (
              <Card key={item.step} className="border-[#DDD5C7] hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="text-2xl font-bold text-[#D4A574] mb-2">{item.step}</div>
                  <CardTitle className="text-[#2C3E2F] text-lg">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#5A6B5E]">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <PilotPartners />

      <Footer />
    </div>
  );
};

export default ProductsPage;
